import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';
import { PMTask } from '../types/pm';
import { createProjectItemStore } from './createProjectItemStore';

export const useTaskStore = createProjectItemStore<PMTask>('tasks');

// 「我的任務」Store - 取得分配給當前使用者的任務
interface MyTasksStore {
  myTasks: PMTask[];
  isLoading: boolean;
  lastFetch: number | null;

  fetchMyTasks: (force?: boolean) => Promise<void>;
  /** 任務異動後標記為過期，下次進「我的任務」時重抓 */
  invalidate: () => void;
}

export const useMyTasksStore = create<MyTasksStore>()(
  persist(
    (set, get) => ({
      myTasks: [],
      isLoading: false,
      lastFetch: null,

      fetchMyTasks: async (force = false) => {
        const { lastFetch, isLoading } = get();
        const now = Date.now();

        // 如果正在載入，避免重複請求
        if (isLoading) {
          return;
        }

        // 快取 5 分鐘
        if (!force && lastFetch && (now - lastFetch < 5 * 60 * 1000)) {
          return;
        }

        set({ isLoading: true });

        try {
          // 使用統一的 api client (參考 CRM 模式)
          const { default: api } = await import('../services/api');
          const response = await api.get(`/tasks/my-tasks`);

          const data = response.data;
          const tasksData = Array.isArray(data) ? data : (data.tasks || data.data || []);

          set({
            myTasks: tasksData,
            lastFetch: now,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to fetch my tasks:', error);
          set({ isLoading: false });
          toast.error(i18n.t('common.messages.fetchFailed'));
        }
      },

      invalidate: () => {
        set({ lastFetch: null });
      },
    }),
    {
      name: 'pm-my-tasks-storage',
      partialize: (state) => ({
        myTasks: state.myTasks,
        lastFetch: state.lastFetch,
      }),
    }
  )
);
